import Navigation from '@/components/Navigation';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import ROIResultsCapture from '@/components/ROIResultsCapture';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Building2,
  CheckCircle,
  ArrowRight,
  Database,
  FileText,
  Zap,
  Users,
  Layers,
  Network,
  Shield,
  ChevronDown,
} from 'lucide-react';

const PropertyManagement = () => {
  const [roiInputs, setRoiInputs] = useState({
    invoices: 1000,
    avgCost: 12,
    exceptionRate: 30,
    propertyCount: 15,
    staffRate: 25,
  });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentCost = roiInputs.invoices * roiInputs.avgCost;
  const hoursSpentPerMonth = roiInputs.invoices * 0.545;
  const exceptionsHandledManually = Math.round((roiInputs.invoices * roiInputs.exceptionRate) / 100);

  const coordinationCost = roiInputs.propertyCount * 10 * roiInputs.staffRate;
  const processingTimeReduction = 0.75;
  const newProcessingCost = currentCost * (1 - processingTimeReduction);
  const processingCostSavings = currentCost - newProcessingCost;

  const hoursSaved = hoursSpentPerMonth * 0.75;
  const exceptionsSaved = Math.round(exceptionsHandledManually * 0.9);
  const exceptionResolutionSavings = (exceptionsSaved * 20) / 60 * roiInputs.staffRate;

  const laborSavings = hoursSaved * roiInputs.staffRate;
  const totalMonthlySavings = laborSavings + exceptionResolutionSavings + coordinationCost + processingCostSavings;
  const ezCloudCost = 2000;
  const netSavings = totalMonthlySavings - ezCloudCost;
  const annualSavings = netSavings * 12;
  const roi = (annualSavings / (ezCloudCost * 12)) * 100;
  const paybackMonths = ezCloudCost / netSavings;

  const painPoints = [
    {
      icon: <FileText className="w-6 h-6 text-accent" />,
      title: 'Construction contractor invoices need certificates attached',
      body: "Contractor invoices carry insurance certificates, W-9s, and lien waivers. When AP staff have to chase documentation before coding, invoice cycle time balloons. EZ Cloud flags supplier records as \"certificate-required\" at data entry, blocks approvals if certificates are missing or expired, and stores supplemental documents at the supplier level so they travel with every invoice.",
    },
    {
      icon: <Zap className="w-6 h-6 text-accent" />,
      title: 'Month-end utility invoice volume overwhelms AP',
      body: 'Utility invoices arrive in bursts, are low-dollar but high-count, and require consistent GL coding across dozens of properties. Manual coding at this cadence produces errors. EZ Cloud codes utility invoices using per-supplier defaults and per-property overrides, so the accountant only reviews exceptions.',
    },
    {
      icon: <Network className="w-6 h-6 text-accent" />,
      title: 'Approval routing by property, not by GL account',
      body: "Property companies need approvals routed to property managers, not just GL-account owners. Most generic AP tools can only route by amount or GL code. EZ Cloud routes by supplier, property, amount, and entity — in any combination — and can auto-assign approvers at the supplier level.",
    },
    {
      icon: <Database className="w-6 h-6 text-accent" />,
      title: 'Yardi integration gaps in generic AP tools',
      body: "Most AP automation tools connect to Yardi via nightly batch or a loose file-drop, which leaves images in one system, invoice metadata in another, and reconciliation as manual clean-up. EZ Cloud's Nexus Integration Layer writes bi-directionally into Yardi: invoice metadata, intercompany records, ACH payment data, and linked images flow into Yardi; expense accounts, PO numbers, active vendors, and property details flow back.",
    },
    {
      icon: <Layers className="w-6 h-6 text-accent" />,
      title: 'Intercompany handling is manual in many rollouts',
      body: 'Standardizing intercompany records across properties is a source of constant cleanup. EZ Cloud handles intercompany at the point of coding — a single invoice splits into the correct entity and property records, posted together.',
    },
    {
      icon: <Users className="w-6 h-6 text-accent" />,
      title: 'AP approver licensing gets expensive',
      body: 'Yardi licenses for occasional approvers are hard to justify. EZ Cloud approvers do not need Yardi licenses — approval happens inside EZ Cloud and posts into Yardi on approval.',
    },
  ];

  const howItWorks = [
    {
      title: 'Yardi-native posting',
      body: 'Invoices, intercompany records, ACH, and images post directly through a prebuilt, EZ Cloud-maintained integration. Not a nightly batch.',
    },
    {
      title: 'Per-property approval routing',
      body: 'Build approval chains by property, entity, GL account, and amount — in any combination.',
    },
    {
      title: 'Auto-assigned approvers by supplier',
      body: "A supplier record maps directly to the right approver, so AP staff don't route manually.",
    },
    {
      title: 'Supplemental docs at the supplier level',
      body: 'Insurance certificates, W-9s, and lien waivers attached to suppliers flow with every invoice. Expiration flags block posting if docs are out of date.',
    },
    {
      title: 'Bi-directional Yardi sync',
      body: 'Active vendors, expense accounts, PO numbers, and property details stay current in EZ Cloud; invoice and payment data flows back into Yardi Voyager.',
    },
    {
      title: 'No approver Yardi licenses required',
      body: 'Approvers use EZ Cloud only.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [] as Array<{
      '@type': 'Question';
      name: string;
      acceptedAnswer: { '@type': 'Answer'; text: string };
    }>,
  };

  const faqs = [
    {
      question: 'Does EZ Cloud post invoices directly into Yardi Voyager?',
      answer:
        "Yes. EZ Cloud writes invoice metadata, intercompany records, ACH payment data, and linked invoice images directly into Yardi via the Nexus Integration Layer — a prebuilt integration we build and maintain. Posting isn't a batch job; invoices flow through as they're approved.",
    },
    {
      question: 'How does EZ Cloud handle construction insurance certificates?',
      answer:
        'Insurance certificates and other supplemental documents attach to the supplier record in EZ Cloud, not to individual invoices. Every invoice from that supplier automatically carries the current certificate. Expiration dates are tracked, and approvals can be blocked if a certificate is expired or missing.',
    },
    {
      question: 'Can we route invoices by property rather than just by GL account?',
      answer:
        'Yes. Approval routing can be configured by any combination of supplier, property, entity, GL account, and amount. The most common property-management pattern is a per-property approver chain driven by the supplier record.',
    },
    {
      question: 'Does EZ Cloud work with Yardi Payscan?',
      answer:
        'EZ Cloud replaces or complements Payscan depending on how you currently use it. For customers using Payscan today, EZ Cloud can coexist during a transition and then absorb Payscan workflows once integration is stable.',
    },
    {
      question: 'How does multi-entity intercompany work?',
      answer:
        'An invoice can split into multiple entity and property records at the point of coding. EZ Cloud posts the intercompany record into Yardi alongside the base invoice, so the accounting entry is complete on post, not fixed up after.',
    },
    {
      question: "What's implementation like for a mid-size property company?",
      answer:
        'A typical mid-size property company goes live in 6–12 weeks. A near-standard Yardi setup can be closer to 6 weeks; larger or more customised environments run 10–12. The phases are: Yardi integration setup, supplier and GL master data migration, approval workflow configuration, user training, and parallel run / cut-over.',
    },
    {
      question: 'Do approvers need Yardi licenses?',
      answer:
        'No. Approvers interact only with EZ Cloud — mobile-friendly web or email approval. The Yardi posting happens via the integration after approval, so approvers never touch Yardi directly.',
    },
    {
      question: 'Can approvals work on mobile?',
      answer:
        'Yes. Approvers receive email notifications with one-click approve/reject and a web view of the invoice. Mobile-first web UI is supported.',
    },
    {
      question: 'What about utility invoices — are those different?',
      answer:
        'Utility invoices get per-supplier coding defaults with per-property overrides. Because utility invoices are high-count but low-dollar, EZ Cloud codes them automatically and only surfaces exceptions (a rate change, a missing property allocation) for human review.',
    },
    {
      question: 'How secure is the Yardi integration?',
      answer:
        "The integration runs over authenticated API calls using Yardi's supported interfaces. Data at rest is encrypted. EZ Cloud operates in a SOC 2 Type II certified environment.",
    },
  ];

  faqSchema.mainEntity = faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  }));

  return (
    <div className="min-h-screen">
      <SEO
        title="AP Automation for Property Management, Yardi-native | EZ Cloud"
        description="Multi-property GL coding, approval routing by property, month-end utility throughput, and contractor documentation — handled natively inside your Yardi Voyager workflow."
        canonical="https://ezcloud.ai/industries/property-management"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Industries', url: 'https://ezcloud.ai/#industries' },
          { name: 'Property Management', url: 'https://ezcloud.ai/industries/property-management' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* HERO */}
      <section className="bg-hero-gradient text-white min-h-[80vh] relative overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto px-4 relative py-20">
          <div className="max-w-4xl space-y-8">
            <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2 text-sm">
              <Building2 className="w-4 h-4 mr-2" />
              Built for Property Management
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
              Accounts payable automation for property management,
              <span className="text-accent"> Yardi-native</span>
            </h1>

            <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl">
              Multi-property GL coding, approval routing by property, utility-invoice throughput at month-end, and construction-contractor documentation — handled natively inside your Yardi Voyager workflow.
            </p>

            <p className="text-base lg:text-lg text-white/80 leading-relaxed max-w-3xl">
              Housing Authority of Covington runs EZ Cloud on Yardi Voyager. 7-day cycles dropped to 2 days, invoice errors fell 80%, and they save roughly $79,000 a year in labor.{' '}
              <Link to="/customers/hacov" className="text-accent hover:underline font-medium">
                Read the case study →
              </Link>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book a 30-minute discovery call
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="font-semibold px-8 py-6 text-lg"
                asChild
              >
                <Link to="/integrations/yardi">See the Yardi integration</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              The AP problems property managers actually have
            </h2>
            <p className="text-lg text-gray-600">
              And how EZ Cloud handles each of them inside Yardi.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {painPoints.map((p, i) => (
              <Card key={i} className="border border-gray-200 bg-white h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      {p.icon}
                    </div>
                    <CardTitle className="text-xl text-gray-900 leading-snug">
                      {i + 1}. {p.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                How EZ Cloud processes property invoices
              </h2>
            </div>

            <div className="space-y-4">
              {howItWorks.map((step, i) => (
                <div key={i} className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-700">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Button variant="outline" className="border-2" asChild>
                <Link to="/integrations/yardi">
                  See the technical integration details
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER PROOF */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-accent/10 text-accent border-accent/20 px-4 py-2">
                Customer Proof
              </Badge>
            </div>

            <Card className="border border-gray-200 bg-white">
              <CardContent className="p-8 md:p-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Housing Authority of Covington
                </h3>
                <p className="text-gray-600 mb-8">
                  HACOV runs EZ Cloud on Yardi Voyager. Live since 2021.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-accent mb-1">7 → 2 days</div>
                    <div className="text-sm text-gray-600">Invoice processing (71% reduction)</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-accent mb-1">80%</div>
                    <div className="text-sm text-gray-600">Reduction in invoice errors</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-accent mb-1">~$79,000</div>
                    <div className="text-sm text-gray-600">Annual labor savings</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-accent mb-1">3 → 25+</div>
                    <div className="text-sm text-gray-600">Active users</div>
                  </div>
                </div>

                <blockquote className="border-l-4 border-accent pl-4 italic text-gray-700 mb-6">
                  EZ Cloud has made my life in a busy accounts payable environment more efficient. The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together but they have, and it has been seamless.
                </blockquote>
                <p className="text-sm text-gray-600 mb-6">
                  — Denise Steffen, Staff Accountant, Housing Authority of Covington
                </p>

                <Button variant="outline" asChild>
                  <Link to="/customers/hacov">
                    Read the full case study
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* PUBLIC HOUSING AUTHORITIES */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Built for public housing authorities
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Public funds demand a tighter audit trail than commercial real estate
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              HACOV is a public housing authority — and the controls a PHA needs are
              the controls EZ Cloud was built around. Every dollar of public or
              HUD-linked funding is traceable from invoice to payment, with a complete,
              user-identified audit log your board, auditors, and funders can rely on.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Fund & program tracking', body: 'Code invoices to the correct fund or HUD program at the line level and carry it through to Yardi.' },
              { title: 'Board-ready approval trail', body: 'Every approval is timestamped and user-identified — defensible in a board meeting or a funder audit.' },
              { title: 'Public-record retention', body: 'Original documents, emails, and approvals retained and exportable for public-records and audit requests.' },
              { title: 'Segregation of duties', body: 'Role-based controls enforce separation between coding, approval, and payment — a standard PHA audit requirement.' },
            ].map((x) => (
              <div key={x.title} className="bg-gray-50 rounded-lg border border-gray-200 p-5">
                <CheckCircle className="w-5 h-5 text-accent mb-2" aria-hidden />
                <div className="font-semibold text-gray-900 mb-1">{x.title}</div>
                <div className="text-sm text-gray-600">{x.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold mb-4">Estimate your savings</h2>
              <p className="text-lg text-white/90">
                Model the impact of EZ Cloud against your current AP cost structure.
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Monthly invoice volume:{' '}
                        <span className="text-primary">{roiInputs.invoices.toLocaleString()}</span>
                      </label>
                      <input
                        type="range"
                        min="200"
                        max="5000"
                        step="100"
                        value={roiInputs.invoices}
                        onChange={(e) => setRoiInputs({ ...roiInputs, invoices: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Current cost per invoice:{' '}
                        <span className="text-primary">${roiInputs.avgCost}</span>
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="25"
                        step="1"
                        value={roiInputs.avgCost}
                        onChange={(e) => setRoiInputs({ ...roiInputs, avgCost: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Exception rate:{' '}
                        <span className="text-primary">{roiInputs.exceptionRate}%</span>
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="50"
                        step="5"
                        value={roiInputs.exceptionRate}
                        onChange={(e) => setRoiInputs({ ...roiInputs, exceptionRate: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Average staff hourly rate:{' '}
                        <span className="text-primary">${roiInputs.staffRate}</span>
                      </label>
                      <input
                        type="range"
                        min="15"
                        max="45"
                        step="1"
                        value={roiInputs.staffRate}
                        onChange={(e) => setRoiInputs({ ...roiInputs, staffRate: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Properties managed:{' '}
                        <span className="text-primary">{roiInputs.propertyCount}</span>
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="100"
                        step="1"
                        value={roiInputs.propertyCount}
                        onChange={(e) => setRoiInputs({ ...roiInputs, propertyCount: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Net monthly savings</div>
                      <div className="text-4xl font-bold text-primary">
                        ${netSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="border-t border-gray-300 pt-4">
                      <div className="text-sm text-gray-600 mb-1">Annual savings</div>
                      <div className="text-3xl font-bold text-accent">
                        ${annualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 border-t border-gray-300 pt-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">ROI</div>
                        <div className="text-2xl font-bold text-green-600">{roi.toFixed(0)}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Payback</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {paybackMonths.toFixed(1)} mo
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 pt-2 border-t border-gray-300">
                      Estimates. Model uses 88–92% touchless processing and up to 90% email-exception prevention as a baseline. Individual results vary by ERP, invoice mix, and process maturity.
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <ROIResultsCapture
                    industry="Property Management"
                    annualSavings={annualSavings}
                    inputs={{
                      'Monthly invoices': roiInputs.invoices.toLocaleString(),
                      'Avg cost per invoice': `$${roiInputs.avgCost}`,
                      'Exception rate': `${roiInputs.exceptionRate}%`,
                      Properties: roiInputs.propertyCount,
                      'Staff hourly rate': `$${roiInputs.staffRate}`,
                    }}
                    buttonLabel="Email my property management ROI report"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud resources for property management
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Yardi-native AP, the HACOV case study, and the integration technology behind multi-property approval routing.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/integrations/yardi"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Yardi Voyager integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native Yardi Voyager AP automation — bi-directional sync, intercompany workflows, insurance certificates, ACH payment data.
                </p>
              </Link>

              <Link
                to="/customers/hacov"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  HACOV case study
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Housing Authority of Covington — 71% faster AP processing, 80% error reduction, $79K annual savings on Yardi Voyager.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers across property management — processing time, audit-readiness, multi-entity wins.
                </p>
              </Link>

              <Link
                to="/resources/roi-calculator"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Full ROI calculator
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Detailed ROI modeling with property-management shortcuts and adjustable assumptions for multi-property AP.
                </p>
              </Link>

              <Link
                to="/integrations/dynamics"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Microsoft Dynamics 365
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  F&O and Business Central integration for property-management companies on Dynamics 365 — multi-entity, financial-dimension coding.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  How native ERP integration works
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time APIs, zero-duplication data flows, SOC 2 compliance — the architecture behind Yardi-native AP.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <Card
                  key={index}
                  className="border border-gray-200 bg-white cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <CardTitle className="text-lg text-left text-gray-900 font-semibold">
                        {faq.question}
                      </CardTitle>
                      <ChevronDown
                        className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </CardHeader>
                  {openFaq === index && (
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Ready to see it on your Yardi instance?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Book a 30-minute discovery call and we'll walk through the integration against your setup.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book a Yardi AP demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="font-semibold px-8 py-6 text-lg"
                asChild
              >
                <Link to="/customers/hacov">Read the HACOV case study</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagement;
